import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { conversationsAPI, hasHistoryContext } from '../services/api';

const PAGE_SIZE = 30;
// Recherche serveur déclenchée après une courte pause de frappe
export const SEARCH_DEBOUNCE_MS = 300;

// Liste triée par dernière activité (updated_at puis id), comme le serveur
const byActivity = (a, b) =>
  (b.updated_at || '').localeCompare(a.updated_at || '') || String(b.id).localeCompare(String(a.id));

/**
 * Historique des conversations (UI-02) : liste paginée, recherche serveur,
 * renommage, suppression, conversation active.
 *
 * Disponible seulement avec un contexte complet (entreprise_id + user_api_hash dans
 * l'URL) : sans lui, `enabled` vaut false et rien n'est appelé.
 */
export function useConversations(userContext) {
  const enabled = hasHistoryContext(userContext);
  const contextRef = useRef(userContext);
  contextRef.current = userContext;

  const [items, setItems] = useState([]);
  const [status, setStatus] = useState(enabled ? 'loading' : 'disabled'); // loading | ready | error | disabled
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const activeIdRef = useRef(activeId);
  activeIdRef.current = activeId;
  // Incrémenté à chaque changement de conversation (nouvelle, sélection) : une
  // création arrivée après coup ne s'impose pas à la conversation affichée
  const epochRef = useRef(0);
  const creatingRef = useRef(null);
  // Identifiant de la conversation en cours de création (proposé par l'interface, lot 1)
  const creatingIdRef = useRef(null);
  const queryRef = useRef(debouncedQuery);
  queryRef.current = debouncedQuery;
  // Génération de la première page : une page suivante demandée avant une nouvelle
  // recherche (ou un rechargement) ne se mélange pas aux nouveaux résultats
  const listSeqRef = useRef(0);

  // ── Recherche : attente de fin de frappe ─────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // ── Première page (au chargement, à chaque recherche, sur « Réessayer ») ──
  useEffect(() => {
    if (!enabled) {
      setStatus('disabled');
      return undefined;
    }
    listSeqRef.current += 1;
    const controller = new AbortController();
    setStatus('loading');
    setError(null);
    conversationsAPI
      .list({ q: debouncedQuery, limit: PAGE_SIZE }, contextRef.current, controller.signal)
      .then((data) => {
        setItems(data.conversations ?? []);
        setNextCursor(data.next_cursor ?? null);
        setStatus('ready');
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setError(err.message || 'Erreur réseau');
        setStatus('error');
      });
    return () => controller.abort();
  }, [enabled, debouncedQuery, reloadKey]);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    const seq = listSeqRef.current;
    setLoadingMore(true);
    try {
      const data = await conversationsAPI.list(
        { q: queryRef.current, limit: PAGE_SIZE, cursor: nextCursor },
        contextRef.current
      );
      if (seq !== listSeqRef.current) return;
      setItems((prev) => {
        const known = new Set(prev.map((c) => c.id));
        return [...prev, ...(data.conversations ?? []).filter((c) => !known.has(c.id))];
      });
      setNextCursor(data.next_cursor ?? null);
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore]);

  /** Met à jour (ou ajoute) une conversation renvoyée par le serveur. */
  const upsert = useCallback((conversation) => {
    if (!conversation?.id) return;
    setItems((prev) => {
      const index = prev.findIndex((c) => c.id === conversation.id);
      if (index === -1) {
        // Une recherche est affichée : une conversation hors résultats n'y entre pas
        return queryRef.current ? prev : [conversation, ...prev].sort(byActivity);
      }
      const next = [...prev];
      next[index] = { ...prev[index], ...conversation };
      return next.sort(byActivity);
    });
  }, []);

  /**
   * Conversation à laquelle rattacher le tour : l'active, sinon une nouvelle,
   * créée côté serveur (titre posé ensuite par le serveur, sans appel au modèle).
   * Lot 1 (vitesse) : `id` est l'identifiant proposé pour une nouvelle conversation ;
   * `enCreation(id)` est appelé aussitôt (pendant l'appel) quand une création est en cours, avec
   * l'identifiant qu'elle aura : la réponse part sans attendre, le serveur la rattache.
   */
  const ensureActive = useCallback(async ({ modelId = null, id = null, enCreation = null } = {}) => {
    if (!hasHistoryContext(contextRef.current)) return null;
    if (activeIdRef.current) return activeIdRef.current;
    if (creatingRef.current) {
      if (creatingIdRef.current) enCreation?.(creatingIdRef.current);
      return creatingRef.current;
    }

    const epoch = epochRef.current;
    const pending = conversationsAPI
      .create({ model_id: modelId, id }, contextRef.current)
      .then(({ conversation }) => {
        if (epochRef.current !== epoch) {
          // L'utilisateur a changé de conversation entre-temps : la conversation
          // créée resterait vide dans l'historique, on la retire
          conversationsAPI.remove(conversation.id, contextRef.current).catch(() => {});
          return null;
        }
        activeIdRef.current = conversation.id;
        setActiveId(conversation.id);
        upsert(conversation);
        return conversation.id;
      })
      .finally(() => {
        // Une nouvelle création (autre conversation) a pu prendre la place entre-temps
        if (creatingRef.current === pending) {
          creatingRef.current = null;
          creatingIdRef.current = null;
        }
      });
    creatingRef.current = pending;
    creatingIdRef.current = id;
    if (id) enCreation?.(id);
    return pending;
  }, [upsert]);

  /** Conversation active au moment de l'appel (lecture hors rendu, sans closure périmée). */
  const getActiveId = useCallback(() => activeIdRef.current, []);

  /** Nouvelle conversation : fil vide, aucune conversation active. */
  const startNew = useCallback(() => {
    epochRef.current += 1;
    creatingRef.current = null;
    creatingIdRef.current = null;
    activeIdRef.current = null;
    setActiveId(null);
  }, []);

  /** Lit une conversation : {conversation, messages} (messages au format du serveur). */
  const fetchOne = useCallback(async (id) => {
    const data = await conversationsAPI.get(id, contextRef.current);
    if (data.conversation) upsert(data.conversation);
    return data;
  }, [upsert]);

  /**
   * Rend `id` active. À appeler dans le même bloc synchrone que l'affichage de ses
   * messages : la sauvegarde ne voit jamais une conversation avec les messages d'une autre.
   */
  const activate = useCallback((id) => {
    epochRef.current += 1;
    creatingRef.current = null;
    creatingIdRef.current = null;
    activeIdRef.current = id;
    setActiveId(id);
  }, []);

  const rename = useCallback(async (id, title) => {
    const clean = title.replace(/\s+/g, ' ').trim();
    if (!clean) throw new Error('Le titre ne peut pas être vide');
    // Optimiste ; le renommage ne change pas l'ordre (updated_at = dernière activité)
    let previous = null;
    setItems((prev) => prev.map((c) => {
      if (c.id !== id) return c;
      previous = c;
      return { ...c, title: clean };
    }));
    try {
      const { conversation } = await conversationsAPI.update(id, { title: clean }, contextRef.current);
      setItems((prev) => prev.map((c) => (c.id === id ? { ...c, ...conversation } : c)));
    } catch (err) {
      if (previous) setItems((prev) => prev.map((c) => (c.id === id ? previous : c)));
      throw err;
    }
  }, []);

  const remove = useCallback(async (id) => {
    try {
      await conversationsAPI.remove(id, contextRef.current);
    } catch (err) {
      // Déjà supprimée (autre onglet) : le résultat attendu est atteint
      if (err.status !== 404) throw err;
    }
    setItems((prev) => prev.filter((c) => c.id !== id));
  }, []);

  return useMemo(() => ({
    enabled,
    items,
    status,
    error,
    hasMore: !!nextCursor,
    loadingMore,
    query,
    setQuery,
    searching: query.trim() !== debouncedQuery,
    activeId,
    getActiveId,
    refresh,
    loadMore,
    upsert,
    ensureActive,
    startNew,
    fetchOne,
    activate,
    rename,
    remove,
  }), [enabled, items, status, error, nextCursor, loadingMore, query, debouncedQuery, activeId,
    getActiveId, refresh, loadMore, upsert, ensureActive, startNew, fetchOne, activate, rename, remove]);
}

// ── Regroupement par ancienneté (Aujourd'hui / 7 derniers jours / Plus ancien) ──
const DAY_MS = 24 * 60 * 60 * 1000;

/** [{id, label, items}] — groupes vides omis. */
export function groupConversations(items, now = new Date()) {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const groups = [
    { id: 'today', label: "Aujourd'hui", items: [] },
    { id: 'week', label: '7 derniers jours', items: [] },
    { id: 'older', label: 'Plus ancien', items: [] },
  ];
  for (const item of items) {
    const at = Date.parse(item.updated_at || item.created_at || '') || 0;
    if (at >= startOfToday) groups[0].items.push(item);
    else if (at >= startOfToday - 6 * DAY_MS) groups[1].items.push(item);
    else groups[2].items.push(item);
  }
  return groups.filter((g) => g.items.length);
}
